import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", address: "", area: "", city: "", photo_url: "", status: "active", notes: "" };

export default function RetailersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/retailers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({...EMPTY, ...r}); setEditId(r.id); setOpen(true); };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      // Retailer/staff photos should NOT be watermarked
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, photo_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
    } catch(err) { toast.error(formatError(err)); }
  };

  const submit = async () => {
    try {
      if (editId) { await api.put(`/retailers/${editId}`, form); toast.success("अपडेट हो गया"); }
      else { await api.post("/retailers", form); toast.success("रिटेलर जोड़ा गया"); }
      setOpen(false); load();
    } catch(err) { toast.error(formatError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("क्या हटाना है?")) return;
    try { await api.delete(`/retailers/${id}`); toast.success("हटा दिया"); load(); }
    catch(err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="retailers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">रिटेलर / एजेंट</h1>
          <p className="text-sm text-slate-500 mt-1">कुल {items.length} रिटेलर</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F172A] hover:bg-[#1E3A8A]" onClick={openNew} data-testid="add-retailer-btn"><Plus className="w-4 h-4 mr-1.5"/>नया रिटेलर</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "रिटेलर संपादित करें" : "नया रिटेलर जोड़ें"}</DialogTitle><DialogDescription>रिटेलर / न्यूज़ एजेंट का विवरण भरें</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>नाम</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="ret-name"/></div>
                <div><Label>फ़ोन</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="ret-phone"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>ईमेल</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/></div>
                <div>
                  <Label>स्थिति</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">सक्रिय</SelectItem>
                      <SelectItem value="inactive">निष्क्रिय</SelectItem>
                      <SelectItem value="pending">प्रतीक्षारत</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>क्षेत्र</Label><Input value={form.area} onChange={(e) => setForm({...form, area: e.target.value})} data-testid="ret-area"/></div>
                <div><Label>शहर</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} data-testid="ret-city"/></div>
              </div>
              <div><Label>पूरा पता</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}/></div>
              <div>
                <Label>फोटो</Label>
                <div className="flex items-center gap-3">
                  <Input value={form.photo_url} onChange={(e) => setForm({...form, photo_url: e.target.value})} placeholder="URL या अपलोड"/>
                  <label className="cursor-pointer flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm hover:bg-slate-50">
                    <Upload className="w-4 h-4"/>
                    <input type="file" accept="image/*" className="hidden" onChange={upload}/>
                  </label>
                </div>
                {form.photo_url && <img src={form.photo_url} alt="" className="mt-2 h-24 w-24 rounded object-cover"/>}
              </div>
              <div><Label>टिप्पणी</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>रद्द</Button>
              <Button onClick={submit} className="bg-[#0F172A] hover:bg-[#1E3A8A]" data-testid="save-retailer">सहेजें</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>कोड</TableHead><TableHead>नाम</TableHead><TableHead>फ़ोन</TableHead>
            <TableHead>क्षेत्र</TableHead><TableHead>शहर</TableHead><TableHead>स्थिति</TableHead><TableHead className="text-right">कार्य</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`ret-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.retailer_code}</TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {r.photo_url && <img src={r.photo_url} alt="" className="w-8 h-8 rounded-full object-cover"/>}
                    {r.name}
                  </div>
                </TableCell>
                <TableCell>{r.phone}</TableCell>
                <TableCell>{r.area}</TableCell>
                <TableCell>{r.city}</TableCell>
                <TableCell><Badge variant={r.status === "active" ? "default" : "secondary"}>{r.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-ret-${r.id}`}><Edit2 className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r.id)} data-testid={`del-ret-${r.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">कोई रिटेलर नहीं</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
